<template>
  <div class="px-4 sm:px-6 lg:px-8 bg-white py-6 rounded-lg shadow-sm h-full overflow-auto">
    <!-- Cabeçalho -->
    <div class="sm:flex sm:items-center sm:justify-between pb-5 border-b border-gray-200">
      <div>
        <h1 class="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <CalendarDaysIcon class="h-7 w-7 text-indigo-600" />
          Minha Agenda & Bloqueios
        </h1>
        <p class="mt-1 text-sm text-gray-600">
          Cadastre seus períodos de indisponibilidade (almoço, médico, férias, etc.) para que o sistema não agende visitas nesses horários.
        </p>
      </div>
      <div class="mt-4 sm:mt-0 flex gap-3">
        <button
          @click="abrirModalCriacao"
          type="button"
          class="inline-flex items-center justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700 focus:outline-none transition-colors"
        >
          <PlusIcon class="h-5 w-5 mr-1" />
          Novo Bloqueio
        </button>
        <button
          @click="carregarBloqueios"
          type="button"
          class="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none transition-colors"
        >
          Atualizar
        </button>
      </div>
    </div>

    <!-- Tabela de Bloqueios -->
    <div class="mt-6 flex flex-col">
      <div class="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
        <div class="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
          <div class="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
            <table class="min-w-full divide-y divide-gray-300">
              <thead class="bg-gray-50">
                <tr>
                  <th scope="col" class="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">Tipo</th>
                  <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Período</th>
                  <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Motivo / Detalhes</th>
                  <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Status</th>
                  <th scope="col" class="relative py-3.5 pl-3 pr-4 sm:pr-6 text-right font-semibold text-gray-900">Ações</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-200 bg-white" v-if="!loading">
                <tr v-for="b in bloqueios" :key="b.id" class="hover:bg-gray-50 transition-colors">
                  <td class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                    <span
                      class="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold capitalize"
                      :class="getTipoBadgeClass(b.tipo)"
                    >
                      {{ traduzirTipo(b.tipo) }}
                    </span>
                    <span v-if="b.dia_inteiro" class="ml-2 inline-flex items-center rounded-full bg-purple-50 px-2 py-0.5 text-xs font-medium text-purple-700 ring-1 ring-inset ring-purple-700/10">
                      Dia Inteiro
                    </span>
                  </td>
                  <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-600">
                    <div class="font-medium text-gray-900">{{ formatarPeriodo(b) }}</div>
                  </td>
                  <td class="px-3 py-4 text-sm text-gray-600">
                    {{ b.motivo || 'Sem observações adicionais.' }}
                  </td>
                  <td class="whitespace-nowrap px-3 py-4 text-sm">
                    <span
                      class="inline-flex rounded-full px-2 text-xs font-semibold leading-5"
                      :class="b.status === 'ativo' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'"
                    >
                      {{ b.status === 'ativo' ? 'Ativo' : 'Cancelado' }}
                    </span>
                  </td>
                  <td class="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                    <button
                      @click="removerBloqueio(b.id)"
                      class="text-red-600 hover:text-red-900 inline-flex items-center gap-1 font-semibold"
                    >
                      <TrashIcon class="h-4 w-4" />
                      Remover
                    </button>
                  </td>
                </tr>
                <tr v-if="bloqueios.length === 0">
                  <td colspan="5" class="px-3 py-8 text-center text-sm text-gray-500">
                    <div class="flex flex-col items-center justify-center">
                      <CalendarDaysIcon class="h-10 w-10 text-gray-300 mb-2" />
                      <p class="font-medium text-gray-600">Nenhum bloqueio cadastrado na sua agenda.</p>
                      <p class="text-xs text-gray-400 mt-1">Sua agenda está totalmente disponível para agendamentos.</p>
                    </div>
                  </td>
                </tr>
              </tbody>
              <tbody v-else>
                <tr>
                  <td colspan="5" class="px-3 py-8 text-center text-sm text-gray-500">
                    Carregando bloqueios da sua agenda...
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal de Criação de Bloqueio -->
    <TransitionRoot as="template" :show="modalAberto">
      <Dialog as="div" class="relative z-50" @close="modalAberto = false">
        <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100" leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
          <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </TransitionChild>

        <div class="fixed inset-0 z-10 overflow-y-auto">
          <div class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95" enter-to="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200" leave-from="opacity-100 translate-y-0 sm:scale-100" leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
              <DialogPanel class="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                <div>
                  <div class="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-indigo-100">
                    <CalendarDaysIcon class="h-6 w-6 text-indigo-600" />
                  </div>
                  <div class="mt-3 text-center sm:mt-5">
                    <DialogTitle as="h3" class="text-lg font-semibold leading-6 text-gray-900">
                      Cadastrar Bloqueio de Horário
                    </DialogTitle>
                    <p class="text-sm text-gray-500 mt-1">
                      Informe o período em que você não estará disponível para visitas.
                    </p>
                  </div>
                </div>

                <form @submit.prevent="salvarBloqueio" class="mt-5 space-y-4">
                  <!-- Tipo de Bloqueio -->
                  <div>
                    <label class="block text-sm font-medium text-gray-700">Tipo de Ausência / Bloqueio</label>
                    <select
                      v-model="form.tipo"
                      required
                      class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    >
                      <option value="almoco">Horário de Almoço</option>
                      <option value="medico">Consulta / Exame Médico</option>
                      <option value="particular">Compromisso Particular</option>
                      <option value="ferias">Férias</option>
                      <option value="folga">Folga / Descanso</option>
                      <option value="outro">Outro Motivo</option>
                    </select>
                  </div>

                  <!-- Checkbox Dia Inteiro -->
                  <div class="flex items-center">
                    <input
                      id="dia_inteiro"
                      type="checkbox"
                      v-model="form.dia_inteiro"
                      class="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    <label for="dia_inteiro" class="ml-2 block text-sm font-medium text-gray-700">
                      Bloquear o Dia Inteiro
                    </label>
                  </div>

                  <!-- Se for Dia Inteiro -->
                  <div v-if="form.dia_inteiro" class="grid grid-cols-2 gap-4">
                    <div>
                      <label class="block text-sm font-medium text-gray-700">Data Inicial</label>
                      <input
                        type="date"
                        v-model="form.data_inicio_dia"
                        required
                        class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      />
                    </div>
                    <div>
                      <label class="block text-sm font-medium text-gray-700">Data Final</label>
                      <input
                        type="date"
                        v-model="form.data_fim_dia"
                        required
                        class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      />
                    </div>
                  </div>

                  <!-- Se for por Horário -->
                  <div v-else class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label class="block text-sm font-medium text-gray-700">Início</label>
                      <input
                        type="datetime-local"
                        v-model="form.data_inicio"
                        required
                        class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      />
                    </div>
                    <div>
                      <label class="block text-sm font-medium text-gray-700">Fim</label>
                      <input
                        type="datetime-local"
                        v-model="form.data_fim"
                        required
                        class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      />
                    </div>
                  </div>

                  <!-- Motivo / Observação -->
                  <div>
                    <label class="block text-sm font-medium text-gray-700">Motivo / Observação (Opcional)</label>
                    <textarea
                      v-model="form.motivo"
                      rows="2"
                      placeholder="Ex: Consulta médica agendada no Hospital..."
                      class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    ></textarea>
                  </div>

                  <!-- Botões -->
                  <div class="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
                    <button
                      type="submit"
                      :disabled="salvando"
                      class="inline-flex w-full justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none sm:col-start-2 sm:text-sm disabled:opacity-50"
                    >
                      {{ salvando ? 'Salvando...' : 'Salvar Bloqueio' }}
                    </button>
                    <button
                      type="button"
                      @click="modalAberto = false"
                      class="mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none sm:col-start-1 sm:mt-0 sm:text-sm"
                    >
                      Cancelar
                    </button>
                  </div>
                </form>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </TransitionRoot>
  </div>
</template>

<script>
import ConsultorBloqueiosRepository from '@/services/ConsultorBloqueiosRepository.vue';
import { CalendarDaysIcon, PlusIcon, TrashIcon } from '@heroicons/vue/24/outline';
import { Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot } from '@headlessui/vue';
import Swal from 'sweetalert2';

export default {
  name: 'MeusBloqueiosView',
  components: {
    CalendarDaysIcon,
    PlusIcon,
    TrashIcon,
    Dialog,
    DialogPanel,
    DialogTitle,
    TransitionChild,
    TransitionRoot
  },
  data() {
    return {
      repository: new ConsultorBloqueiosRepository(),
      onlineUser: JSON.parse(window.localStorage.getItem('myUser') || '{}'),
      token: window.localStorage.getItem('token') || (JSON.parse(window.localStorage.getItem('myUser') || '{}').token),
      loading: true,
      salvando: false,
      bloqueios: [],
      modalAberto: false,
      form: {
        tipo: 'particular',
        motivo: '',
        dia_inteiro: false,
        data_inicio: '',
        data_fim: '',
        data_inicio_dia: '',
        data_fim_dia: '',
      }
    };
  },
  mounted() {
    this.carregarBloqueios();
  },
  methods: {
    getUserEmail() {
      return this.onlineUser.user?.email || this.onlineUser.email || '';
    },
    async carregarBloqueios() {
      this.loading = true;
      const email = this.getUserEmail();
      try {
        const response = await this.repository.getBloqueios(this.token, {
          consultor_email: email,
          per_page: 100
        });
        this.bloqueios = response.data.data || response.data || [];
      } catch (error) {
        console.error('Erro ao carregar bloqueios:', error);
        Swal.fire('Erro', 'Falha ao carregar seus bloqueios de agenda.', 'error');
      } finally {
        this.loading = false;
      }
    },
    abrirModalCriacao() {
      const hoje = new Date().toISOString().split('T')[0];
      this.form = {
        tipo: 'particular',
        motivo: '',
        dia_inteiro: false,
        data_inicio: `${hoje}T12:00`,
        data_fim: `${hoje}T13:00`,
        data_inicio_dia: hoje,
        data_fim_dia: hoje,
      };
      this.modalAberto = true;
    },
    async salvarBloqueio() {
      const email = this.getUserEmail();
      if (!email) {
        Swal.fire('Erro', 'Usuário não autenticado corretamente.', 'error');
        return;
      }

      let payload = {
        consultor_email: email,
        tipo: this.form.tipo,
        motivo: this.form.motivo,
        dia_inteiro: this.form.dia_inteiro,
      };

      if (this.form.dia_inteiro) {
        payload.data_inicio = `${this.form.data_inicio_dia} 00:00:00`;
        payload.data_fim = `${this.form.data_fim_dia} 23:59:59`;
      } else {
        payload.data_inicio = this.form.data_inicio.replace('T', ' ') + ':00';
        payload.data_fim = this.form.data_fim.replace('T', ' ') + ':00';
      }

      this.salvando = true;
      try {
        await this.repository.criarBloqueio(this.token, payload);
        Swal.fire('Sucesso!', 'Bloqueio de horário registrado com sucesso.', 'success');
        this.modalAberto = false;
        await this.carregarBloqueios();
      } catch (error) {
        console.error('Erro ao salvar bloqueio:', error);
        const msg = error.response?.data?.message || 'Erro ao registrar bloqueio de horário.';
        Swal.fire('Atenção', msg, 'error');
      } finally {
        this.salvando = false;
      }
    },
    async removerBloqueio(id) {
      const result = await Swal.fire({
        title: 'Remover Bloqueio?',
        text: 'Seu horário voltará a ficar disponível para novos agendamentos.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Sim, remover',
        cancelButtonText: 'Cancelar'
      });

      if (!result.isConfirmed) return;

      try {
        await this.repository.removerBloqueio(this.token, id);
        Swal.fire('Removido!', 'Bloqueio excluído com sucesso.', 'success');
        await this.carregarBloqueios();
      } catch (error) {
        console.error('Erro ao remover bloqueio:', error);
        Swal.fire('Erro', 'Falha ao remover o bloqueio.', 'error');
      }
    },
    traduzirTipo(tipo) {
      const map = {
        almoco: 'Almoço',
        medico: 'Médico / Exame',
        particular: 'Particular',
        ferias: 'Férias',
        folga: 'Folga',
        outro: 'Outro'
      };
      return map[tipo] || tipo;
    },
    getTipoBadgeClass(tipo) {
      const map = {
        almoco: 'bg-yellow-100 text-yellow-800',
        medico: 'bg-red-100 text-red-800',
        particular: 'bg-blue-100 text-blue-800',
        ferias: 'bg-green-100 text-green-800',
        folga: 'bg-purple-100 text-purple-800',
        outro: 'bg-gray-100 text-gray-800'
      };
      return map[tipo] || 'bg-gray-100 text-gray-800';
    },
    formatarPeriodo(b) {
      if (b.dia_inteiro) {
        const dInicio = new Date(b.data_inicio).toLocaleDateString('pt-BR');
        const dFim = new Date(b.data_fim).toLocaleDateString('pt-BR');
        return dInicio === dFim ? dInicio : `${dInicio} até ${dFim}`;
      }
      const dInicio = new Date(b.data_inicio);
      const dFim = new Date(b.data_fim);
      return `${dInicio.toLocaleDateString('pt-BR')} ${dInicio.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })} às ${dFim.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`;
    }
  }
};
</script>
